export const holdingUnitLifecycleStatusValues = [
  'open',
  'ready',
  'shipped',
  'retired',
  'superseded',
] as const;
export type HoldingUnitLifecycleStatus = (typeof holdingUnitLifecycleStatusValues)[number];

export const holdingUnitAvailabilityReasonValues = [
  'AVAILABLE',
  'STATUS_SHIPPED',
  'STATUS_RETIRED',
  'RESERVED_LOAD_SESSION',
  'RESERVED_SHIPMENT',
  'RESERVED_OTHER',
] as const;
export type HoldingUnitAvailabilityReason = (typeof holdingUnitAvailabilityReasonValues)[number];

export const inventoryTrackingModeValues = [
  'not_tracked',
  'tracked_optional',
  'tracked_required',
] as const;
export type InventoryTrackingMode = (typeof inventoryTrackingModeValues)[number];

export const holdingUnitTypeValues = ['source', 'staging', 'product', 'leftover'] as const;
export type HoldingUnitType = (typeof holdingUnitTypeValues)[number];

export const holdingUnitIdentityModeValues = ['generic', 'fixed_asset_cycle'] as const;
export type HoldingUnitIdentityMode = (typeof holdingUnitIdentityModeValues)[number];

export const holdingUnitContainerKindValues = [
  'loose',
  'bag',
  'heavy_container',
  'staging_container',
  'none',
] as const;
export type HoldingUnitContainerKind = (typeof holdingUnitContainerKindValues)[number];

export const holdingUnitProductKindValues = ['bale', 'paca', 'none'] as const;
export type HoldingUnitProductKind = (typeof holdingUnitProductKindValues)[number];

export interface HoldingUnitProfile {
  unitType: HoldingUnitType;
  identityMode: HoldingUnitIdentityMode;
  containerKind: HoldingUnitContainerKind;
  productKind: HoldingUnitProductKind;
}

export interface HoldingUnitCapabilities {
  canReceiveIntake: boolean;
  canReceiveTransfer: boolean;
  canTransferOut: boolean;
  canAdjust: boolean;
  canReady: boolean;
  canShip: boolean;
  canRetire: boolean;
  canBuildProduct: boolean;
  canHoldLeftover: boolean;
  canReopen: boolean;
  canDisassemble: boolean;
}

export interface HoldingUnitProfileSnapshotMetadata {
  unitProfileId?: string | null;
  unitProfileRevision?: number | null;
  unitProfileDisplayName?: string | null;
  unitProfileTemplateKey?: UnitProfileTemplateKey | null;
  capabilityPolicy?: HoldingUnitCapabilities | null;
}

export const holdingUnitWeightInputModeValues = ['grossTotal', 'netDelta'] as const;
export type HoldingUnitWeightInputMode = (typeof holdingUnitWeightInputModeValues)[number];

export const holdingUnitAdjustmentTypeValues = ['shrink', 'surplus'] as const;
export type HoldingUnitAdjustmentType = (typeof holdingUnitAdjustmentTypeValues)[number];

export const holdingUnitLoadSessionStatusValues = ['open', 'ready_to_depart', 'canceled'] as const;
export type HoldingUnitLoadSessionStatus = (typeof holdingUnitLoadSessionStatusValues)[number];

export const holdingUnitLoadSessionGroupClaimStatusValues = [
  'unclaimed',
  'claimed',
  'consumed',
] as const;
export type HoldingUnitLoadSessionGroupClaimStatus =
  (typeof holdingUnitLoadSessionGroupClaimStatusValues)[number];

export const holdingUnitLoadSessionItemSelectionMethodValues = [
  'scan',
  'autopick',
  'manual_pick',
  'no_reweigh',
] as const;
export type HoldingUnitLoadSessionItemSelectionMethod =
  (typeof holdingUnitLoadSessionItemSelectionMethodValues)[number];

export const holdingUnitLoadSessionMeasuredDiscrepancyActionValues = [
  'committed',
  'discarded',
] as const;
export type HoldingUnitLoadSessionMeasuredDiscrepancyAction =
  (typeof holdingUnitLoadSessionMeasuredDiscrepancyActionValues)[number];

export const holdingUnitLoadSessionMeasuredDiscrepancyStateValues = [
  'unresolved',
  'committed',
  'discarded',
  'alignedExternally',
] as const;
export type HoldingUnitLoadSessionMeasuredDiscrepancyState =
  (typeof holdingUnitLoadSessionMeasuredDiscrepancyStateValues)[number];

export const holdingUnitMeasuredDiscrepancyCommittedWeightEventTypeValues = [
  'holdingUnitWeightCorrected',
] as const;
export type HoldingUnitMeasuredDiscrepancyCommittedWeightEventType =
  (typeof holdingUnitMeasuredDiscrepancyCommittedWeightEventTypeValues)[number];

export const holdingUnitLoadSessionMatchCandidateClassificationValues = [
  'EXACT',
  'DOWNWARD_WITHIN_TOLERANCE',
  'OUTSIDE_TOLERANCE',
  'UPWARD_SUGGESTION',
] as const;
export type HoldingUnitLoadSessionMatchCandidateClassification =
  (typeof holdingUnitLoadSessionMatchCandidateClassificationValues)[number];

export const holdingUnitReservationTargetTypeValues = ['loadSession', 'shipment'] as const;
export type HoldingUnitReservationTargetType =
  (typeof holdingUnitReservationTargetTypeValues)[number];

export interface HoldingUnitIdentifier {
  type: string;
  value: string;
}

export interface HoldingUnitSnapshot
  extends HoldingUnitProfile,
    HoldingUnitProfileSnapshotMetadata {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string;
  yardLocationId: string | null;
  fixedIdAssetId?: string | null;
  lifecycleStatus: HoldingUnitLifecycleStatus;
  capabilities: HoldingUnitCapabilities;
  materialId: string | null;
  identifier: HoldingUnitIdentifier | null;
  identifierType?: string | null;
  tareLb: number | null;
  tareSuggestedLb?: number | null;
  grossLb: number | null;
  netLb: number;
  normalizedPhysicalId?: string | null;
  normalizedScannableIdentifier?: string | null;
  version: number;
  lastEventId: string;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface HoldingUnitReservationTarget {
  reservationType: HoldingUnitReservationTargetType;
  loadSessionId?: string | null;
  shipmentId?: string | null;
}

export interface HoldingUnitReservationState extends HoldingUnitReservationTarget {
  id: string;
  companyId: string;
  unitId: string;
  storeId: string;
  locationId: string;
  reservedAt: string; // ISO datetime
  reservedByUserId: string | null;
}

export interface HoldingUnitLoadSession {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  status: HoldingUnitLoadSessionStatus;
  sessionName: string | null;
  toleranceProfileKey: string | null;
  createdByUserId: string | null;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
  version: number;
}

export interface HoldingUnitLoadSessionMaterialGroup {
  id: string;
  loadSessionId: string;
  companyId: string;
  materialId: string;
  autoPickEnabled: boolean;
  toleranceProfileKey: string | null;
  yardLocationIds: string[] | null;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
  createdByUserId: string | null;
}

export interface HoldingUnitWeightBreakdown {
  grossLb: number | null;
  tareLb: number | null;
  netLb: number | null;
}

export interface HoldingUnitWeightDelta {
  grossLb: number | null;
  tareLb: number | null;
  netLb: number | null;
}

export interface HoldingUnitLoadSessionItem {
  id: string;
  loadSessionId: string;
  groupId: string;
  companyId: string;
  unitId: string;
  selectionMethod: HoldingUnitLoadSessionItemSelectionMethod;
  snapshotNetLbAtSelection: number;
  snapshotGrossLbAtSelection: number | null;
  snapshotTareLbAtSelection: number | null;
  loadMeasuredGrossLb: number | null;
  loadMeasuredNetLb: number | null;
  deltaLossLb: number | null;
  toleranceProfileKey: string | null;
  capturedAt: string; // ISO datetime
  capturedByUserId: string | null;
  wasAutoPicked: boolean;
  measuredDiscrepancyAction: HoldingUnitLoadSessionMeasuredDiscrepancyAction | null;
  measuredDiscrepancyResolvedAt: string | null; // ISO datetime
  measuredDiscrepancyResolvedByUserId: string | null;
  measuredDiscrepancyCommittedWeightEventId: string | null;
  measuredDiscrepancyCommittedWeightEventType: HoldingUnitMeasuredDiscrepancyCommittedWeightEventType | null;
  removedAt: string | null; // ISO datetime
  removedByUserId: string | null;
}

export interface HoldingUnitLoadSessionDiscrepancyReviewItem {
  itemId: string;
  unitId: string;
  firstWeigh: HoldingUnitWeightBreakdown;
  reweighAtLoading: HoldingUnitWeightBreakdown;
  difference: HoldingUnitWeightDelta;
  officialForShipment: HoldingUnitWeightBreakdown;
  state: HoldingUnitLoadSessionMeasuredDiscrepancyState;
  canCommit: boolean;
  commitBlockedReason: string | null;
  resolvedAt: string | null; // ISO datetime
  resolvedByUserId: string | null;
}

export interface HoldingUnitLoadSessionDiscrepancyReview {
  unresolvedCount: number;
  items: HoldingUnitLoadSessionDiscrepancyReviewItem[];
}

export interface HoldingUnitLoadSessionMatchCandidate {
  unitId: string;
  expectedGrossLb: number;
  expectedNetLb: number;
  tareLb: number;
  deltaGrossLb: number;
  classification: HoldingUnitLoadSessionMatchCandidateClassification;
}

export interface HoldingUnitLoadSessionGroupTotals {
  groupId: string;
  unitCount: number;
  firstWeighTotal: HoldingUnitWeightBreakdown;
  reweighTotal: HoldingUnitWeightBreakdown;
  differenceTotal: HoldingUnitWeightDelta;
  officialForShipment: HoldingUnitWeightBreakdown;
}

export interface HoldingUnitInventorySummaryItem {
  materialId: string | null;
  lifecycleStatus: HoldingUnitLifecycleStatus;
  unitCount: number;
  totalNetLb: number;
}

export interface HoldingUnitAssistedMatchCandidate {
  unitId: string;
  lifecycleStatus: HoldingUnitLifecycleStatus;
  materialId: string | null;
  grossLb: number | null;
  tareLb: number | null;
  netLb: number;
  deltaNetLb: number;
  // True only when the top-ranked candidate is a unique exact match (deltaNetLb === 0 with no ties).
  exactMatch: boolean;
}
import type { UnitProfileTemplateKey } from '../unit-profiles/unit-profiles.dto.js';
